"use client";

import {
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

export interface Enrollments4MetaItem {
  id?: string | number;
  meta_priority?: number | string;
  meta_status?: boolean;
  meta_icon?: string;
  meta_icon_alt?: string;
  meta_title?: string;
  meta_description?: string;
  meta_front_background_color?: string;
  meta_front_text_color?: string;
  meta_back_background_color?: string;
  meta_back_text_color?: string;
}

export interface Enrollments4Data {
  id?: string;
  status?: boolean;
  title?: string;
  cta_label?: string;
  cta_url?: string;
  open_in_new_tab?: boolean;
  desktop_image?: string;
  mobile_image?: string;
  background_color?: string;
  title_color?: string;
  title_highlight_color?: string;
  cta_background_color?: string;
  cta_text_color?: string;
  enable_lead_popup?: boolean;
  metadata?: Enrollments4MetaItem[];
}

interface Enrollments4Props {
  data: Enrollments4Data | { data: Enrollments4Data };
}

function resolvePayload(
  data: Enrollments4Props["data"],
): Enrollments4Data | undefined {
  const anyData = data as { data?: Enrollments4Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Enrollments4Data;
  }
  return data as Enrollments4Data;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Wraps `{highlight}` / `{{line}}` segments in a span styled with the supplied
 * highlight color. Mirrors `formatSectionTitle` semantics but applies the color
 * via inline `style=` so admin-driven hex values don't need Tailwind JIT.
 */
function formatTitleWithHighlight(title: string, highlightColor: string): string {
  if (!title) return "";
  const safeColor = highlightColor.replace(/[^#a-zA-Z0-9(),.%\s]/g, "");
  const openTag = `<span style="color:${safeColor}">`;

  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, (_m, inner) => `${openTag}${escapeHtml(inner)}</span><br/>`)
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title
      .replace(/\{/g, openTag)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }

  return escapeHtml(title);
}

interface FlipCardProps {
  item: Enrollments4MetaItem;
  isMobileFlipped: boolean;
  isMobile: boolean | null;
  onMobileToggle: () => void;
  onHover?: () => void;
  titleHighlightColor: string;
}

/**
 * Desktop: pure-CSS hover flip via `md:group-hover:[transform:rotateY(180deg)]`
 * — no React state needed, so multiple cards can be hovered independently.
 * Mobile (< md): tap to toggle, driven by parent-held `isMobileFlipped` state.
 * Back face has a scrollable body for long descriptions.
 */
const FlipCard = ({
  item,
  isMobileFlipped,
  isMobile,
  onMobileToggle,
  onHover,
  titleHighlightColor,
}: FlipCardProps) => {
  const {
    meta_icon,
    meta_icon_alt,
    meta_title = "",
    meta_description = "",
    meta_front_background_color = "#FFFFFF",
    meta_front_text_color = "#0A0A0A",
    meta_back_background_color = "#DB5803",
    meta_back_text_color = "#FFFFFF",
  } = item;

  // Apply the rotation only when mobile is confirmed (isMobile === true).
  // null = pre-hydration, treat as desktop and let CSS hover drive the flip.
  const mobileFlipClass =
    isMobile === true && isMobileFlipped ? "[transform:rotateY(180deg)]" : "";

  return (
    <div
      className={cn(
        "group block w-[160px] h-[184px] md:w-[240px] md:h-[164px] outline-none touch-manipulation cursor-pointer md:cursor-default",
        "[perspective:1000px]",
        "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-main",
      )}
      role="button"
      tabIndex={0}
      aria-label={`${meta_title}. ${isMobile ? "Tap" : "Hover"} to reveal details.`}
      onClick={() => {
        if (isMobile === true) onMobileToggle();
      }}
      onKeyDown={(e) => {
        if (isMobile === true && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onMobileToggle();
        }
      }}
      onMouseEnter={() => {
        if (isMobile === false) onHover?.();
      }}
    >
      <div
        className={cn(
          "relative h-full w-full transition-transform duration-700 [transform-style:preserve-3d]",
          mobileFlipClass,
          "md:group-hover:[transform:rotateY(180deg)]",
        )}
      >
        {/* Front — mirrors IITKGP layout: icon on top + left-bordered title
            block underneath. Border color tracks the front text color. */}
        <div
          className={cn(
            "absolute inset-0 [backface-visibility:hidden]",
            // Only the visible face may take pointer/touch events. iOS Safari
            // still hit-tests a backface-hidden layer, so without this the
            // face rotated away swallows the touch and the page won't scroll.
            isMobile === true && isMobileFlipped && "pointer-events-none",
            "md:group-hover:pointer-events-none",
          )}
          style={{ WebkitBackfaceVisibility: "hidden" }}
        >
          <div
            className={cn(
              "flex h-full w-full flex-col rounded-2xl shadow-[0px_4px_14.8px_0px_rgba(0,0,0,0.15)]",
              "p-[8px] md:p-4",
              "gap-[14px] md:gap-5",
            )}
            style={{
              backgroundColor: meta_front_background_color,
              color: meta_front_text_color,
            }}
          >
            {meta_icon ? (
              <div className="relative size-[32px] md:size-[42px] shrink-0">
                <Image
                  src={meta_icon}
                  alt={meta_icon_alt || meta_title || "icon"}
                  fill
                  className="object-contain"
                  sizes="42px"
                />
              </div>
            ) : null}
            <div
              className={cn(
                "border-l-[0.69px] md:border-l-[1px]",
                "p-[10px]",
                "font-medium text-[14px] md:text-[16px] leading-snug",
              )}
              style={{ borderColor: meta_front_text_color }}
            >
              {meta_title}
            </div>
            {/* Mobile-only CTA arrow — colored with the section title
                highlight color, seated at the card's bottom-left. */}
            <ArrowUpRight
              className="mt-auto size-5 shrink-0 md:hidden"
              style={{ color: titleHighlightColor }}
              strokeWidth={2}
              aria-hidden
            />
          </div>
        </div>

        {/* Back — mirrors IITKGP structure: outer transform layer + inner card
            surface + fixed-height bordered scrollable body. */}
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center [transform:rotateY(180deg)] [backface-visibility:hidden]",
            // Inert until this card is actually flipped (mobile) / hovered
            // (desktop) — see the note on the front face above.
            !(isMobile === true && isMobileFlipped) && "pointer-events-none",
            "md:pointer-events-none md:group-hover:pointer-events-auto",
          )}
          style={{ WebkitBackfaceVisibility: "hidden" }}
        >
          <div
            className={cn(
              "flex h-full w-full flex-col gap-2 rounded-2xl shadow-[0px_4px_14.8px_0px_rgba(0,0,0,0.15)]",
              "p-[8px] md:p-4",
              "items-start justify-between md:items-center md:justify-center",
            )}
            style={{
              backgroundColor: meta_back_background_color,
              color: meta_back_text_color,
            }}
          >
            {/* Bordered scrollable body — fixed height so the scrollbar appears
                inside the card when the description overflows. Border color
                tracks the back text color to match the IITKGP design. */}
            <div
              className={cn(
                // Scrolls vertically but visually hides the scrollbar on both
                // Firefox (`scrollbar-width: none`) and WebKit (`::-webkit-scrollbar`).
                // `overscroll-contain` is desktop-only: on touch it would trap
                // the drag inside the card instead of chaining to the page.
                "overflow-y-auto md:overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                "border-l-[0.69px] md:border-l-[1px]",
                "px-[10px]",
                "h-[118px] md:h-[116px]",
                "font-medium text-[14px] md:text-[16px] leading-snug",
              )}
              style={{ borderColor: meta_back_text_color }}
              // Desktop only: keep a click on the scrollable body from bubbling.
              // On mobile the tap must reach the card so it can flip back.
              onClick={(e) => {
                if (isMobile !== true) e.stopPropagation();
              }}
            >
              {meta_description}
            </div>
            {/* Mobile-only CTA arrow — matches the back (description) text
                color; the container's `justify-between` seats it bottom-left. */}
            <ArrowUpRight
              className="size-5 shrink-0 md:hidden"
              style={{ color: meta_back_text_color }}
              strokeWidth={2}
              aria-hidden
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Enrollments4 = ({ data }: Enrollments4Props) => {
  const dispatch = useDispatch();
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const isMobile = useIsMobile(768);
  const [flippedIndex, setFlippedIndex] = useState<number | null>(null);

  const {
    status = true,
    title = "Why Pursue {this Programme}",
    cta_label = "Get Started",
    cta_url = "",
    open_in_new_tab = true,
    desktop_image = "",
    mobile_image = "",
    background_color = "#FFFFFF",
    title_color = "#0A0A0A",
    title_highlight_color = "#DB5803",
    cta_background_color = "#DB5803",
    cta_text_color = "#FFFFFF",
    enable_lead_popup: enableLeadRaw,
    metadata = [],
  } = sectionData || {};

  const raw = sectionData as Record<string, unknown> | undefined;
  const enableLeadPopup = isCmsShowFlagOn(
    raw?.enable_lead_popup ?? raw?.enableLeadPopup ?? enableLeadRaw,
    true,
  );

  const hrefTrim = (cta_url || "").trim();
  const ctaOpensLeadPopup = enableLeadPopup || !hrefTrim;

  const sortedCards = useMemo(() => {
    if (!Array.isArray(metadata) || metadata.length === 0) return [];
    return [...metadata]
      .filter((m) => m && (m.meta_status === undefined || m.meta_status === true))
      .sort(
        (a, b) =>
          Number(a.meta_priority ?? 0) - Number(b.meta_priority ?? 0),
      );
  }, [metadata]);

  const openLeadFromCta = useCallback(() => {
    ctaClick({
      section: "enrollments_4",
      component: "cta_apply",
      label: cta_label?.trim() || "apply",
      step: "1",
      entity: "enrollments",
      parentEntity: "enrollments_4",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [cta_label, dispatch]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "enrollments_4");
  //   }
  // }, [status]);

  if (!status) return null;

  const ctaClassName = cn(
    "inline-flex shrink-0 items-center gap-2 rounded-md font-semibold shadow-sm transition-opacity",
    "px-4 py-2 text-sm leading-snug hover:opacity-95",
    "md:gap-2.5 md:px-5 md:py-3 md:text-base md:leading-[23px]",
    ctaOpensLeadPopup && "cursor-pointer",
    !ctaOpensLeadPopup && !hrefTrim && "cursor-default opacity-90",
  );

  const ctaIconClass = "size-5 shrink-0 md:size-6";
  const ctaStyle = {
    backgroundColor: cta_background_color,
    color: cta_text_color,
  };

  const renderCta = () => {
    if (ctaOpensLeadPopup) {
      return (
        <button
          type="button"
          className={ctaClassName}
          style={ctaStyle}
          onClick={openLeadFromCta}
        >
          {cta_label}
          <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
        </button>
      );
    }
    if (hrefTrim) {
      return (
        <a
          href={hrefTrim}
          target={open_in_new_tab ? "_blank" : undefined}
          rel={open_in_new_tab ? "noopener noreferrer" : undefined}
          className={ctaClassName}
          style={ctaStyle}
          onClick={() =>
            ctaClick({
              section: "enrollments_4",
              component: "cta_apply_link",
              label: cta_label?.trim() || "apply",
              step: "1",
              entity: "enrollments",
              parentEntity: "enrollments_4",
            })
          }
        >
          {cta_label}
          <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
        </a>
      );
    }
    return (
      <span className={cn(ctaClassName, "cursor-default opacity-90")} style={ctaStyle}>
        {cta_label}
        <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
      </span>
    );
  };

  const desktopBg = (desktop_image || "").trim();
  const mobileBg = (mobile_image || desktop_image || "").trim();

  return (
    <section
      ref={ref}
      data-enrollments="4"
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative flex w-full items-center justify-center bg-cover bg-center bg-no-repeat",
      )}
      style={{
        backgroundColor: background_color,
        backgroundImage: desktopBg ? `url(${desktopBg})` : undefined,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {/* Mobile bg overlay (falls back to desktop_image) */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat md:hidden"
        style={{
          backgroundImage: mobileBg ? `url(${mobileBg})` : undefined,
        }}
        aria-hidden
      />

      <div className="relative z-10 w-full px-5 py-9 md:px-[50px] md:py-[60px]">
        <div className="mx-auto flex w-full max-w-screen-container flex-col items-center justify-center gap-[52px] md:gap-[62px]">
          <div className="flex flex-col items-center justify-center gap-4">
            {title ? (
              <h2
                className="w-full text-center text-[28px] font-semibold leading-[40px] md:text-[32px]"
                style={{ color: title_color }}
                dangerouslySetInnerHTML={{
                  __html: formatTitleWithHighlight(title, title_highlight_color),
                }}
              />
            ) : null}
            {renderCta()}
          </div>

          {sortedCards.length > 0 ? (
            <div className="flex flex-wrap items-center justify-center gap-4">
              {sortedCards.map((item, index) => (
                <FlipCard
                  key={item.id ?? `card-${index}`}
                  item={item}
                  isMobile={isMobile}
                  isMobileFlipped={flippedIndex === index}
                  titleHighlightColor={title_highlight_color}
                  onMobileToggle={() => {
                    ctaClick({
                      section: "enrollments_4",
                      label: item.meta_title ?? `card_${index}`,
                      component: "flip",
                      step: "",
                      entity: "",
                    });
                    setFlippedIndex((prev) => (prev === index ? null : index));
                  }}
                  onHover={() => {
                    ctaClick({
                      section: "enrollments_4",
                      label: item.meta_title ?? `card_${index}`,
                      component: "flip_hover",
                      step: "",
                      entity: "",
                    });
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default Enrollments4;
